<template>
    <v-dialog
        v-model="dialogModel"
        width="900"
        max-width="95%"
        @click:outside.prevent="closeDialog"
    >
        <v-card>
            <v-card-title class="text-h6 text-md-h5 grey lighten-2">
                <v-row>
                    <v-col cols="10">
                        Dentist Profile
                    </v-col>
                    <v-col cols="2" class="text-right">
                        <v-btn icon @click="closeDialog"><v-icon>mdi-close</v-icon></v-btn>
                    </v-col>
                </v-row>
            </v-card-title>
            <v-card-text class="py-5" style="max-height: 75vh; overflow-y: auto;">
                <v-form ref="dentistProfileForm" lazy-validation>
                    <p class="mb-6" style="border-bottom: 1px solid #e0e0e0; padding-bottom: 32px;">
                        This questionnaire is to help build your profile on the “meet the team” segment of the website.
                        Please answer as much as you are able to — the more information you give, the better your profile will be.
                        You do not have to worry about using full sentences or correct punctuation.
                    </p>

                    <v-row class="mb-4">
                        <v-col cols="12" md="6">
                            <h3 class="mb-4">Name <span class="red--text">*</span></h3>
                            <v-text-field
                                v-model="form.name.firstName"
                                placeholder="First Name"
                                outlined
                                dense
                                :rules="firstNameRules"
                                hint="First Name"
                                :persistent-hint="true"
                            />
                        </v-col>
                        <v-col cols="12" md="6" class="d-flex align-end">
                            <v-text-field
                                v-model="form.name.lastName"
                                placeholder="Last Name"
                                outlined
                                dense
                                :rules="lastNameRules"
                                hint="Last Name"
                                :persistent-hint="true"
                            />
                        </v-col>
                    </v-row>

                    <v-row class="mb-4">
                        <v-col cols="12">
                            <h3 class="mb-4">Where were you born? If you were born outside the UK, when did you move here and why? (e.g. study)</h3>
                            <v-textarea
                                v-model="form.whereBorn"
                                placeholder="Your answer"
                                outlined
                                dense
                                auto-grow
                                rows="3"
                            />
                        </v-col>
                    </v-row>

                    <v-row class="mb-4">
                        <v-col cols="12">
                            <h3 class="mb-4">Where did you study? <span class="red--text">*</span></h3>
                            <v-textarea
                                v-model="form.whereStudied"
                                placeholder="Your answer"
                                outlined
                                dense
                                auto-grow
                                rows="3"
                                :rules="[requiredRules]"
                            />
                        </v-col>
                    </v-row>

                    <v-row class="mb-4">
                        <v-col cols="12">
                            <h3 class="mb-4">What level of education do you have and what subject did you study? (e.g. MSc Implantology)</h3>
                            <v-textarea
                                v-model="form.educationLevelAndSubject"
                                placeholder="Your answer"
                                outlined
                                dense
                                auto-grow
                                rows="3"
                            />
                        </v-col>
                    </v-row>

                    <v-row class="mb-4">
                        <v-col cols="12">
                            <h3 class="mb-4">List your dental experience and any areas of dentistry you have a special interest in (e.g. Full arch implants).</h3>
                            <v-textarea
                                v-model="form.dentalExperience"
                                placeholder="Your answer"
                                outlined
                                dense
                                auto-grow
                                rows="3"
                            />
                        </v-col>
                    </v-row>

                    <v-row class="mb-4">
                        <v-col cols="12">
                            <h3 class="mb-4">How do you handle nervous patients?</h3>
                            <v-textarea
                                v-model="form.handleNervousPatients"
                                placeholder="Your answer"
                                outlined
                                dense
                                auto-grow
                                rows="3"
                            />
                        </v-col>
                    </v-row>

                    <v-row class="mb-4">
                        <v-col cols="12">
                            <h3 class="mb-4">What do you do to unwind after a hard day at work?</h3>
                            <v-textarea
                                v-model="form.unwindAfterWork"
                                placeholder="Your answer"
                                outlined
                                dense
                                auto-grow
                                rows="3"
                            />
                        </v-col>
                    </v-row>

                    <v-row class="mb-4">
                        <v-col cols="12">
                            <h3 class="mb-4">What are your weekend hobbies?</h3>
                            <v-textarea
                                v-model="form.weekendHobbies"
                                placeholder="Your answer"
                                outlined
                                dense
                                auto-grow
                                rows="3"
                            />
                        </v-col>
                    </v-row>

                    <v-row class="mb-4">
                        <v-col cols="12">
                            <h3 class="mb-4">Where have you travelled to?</h3>
                            <v-textarea
                                v-model="form.travelledTo"
                                placeholder="Your answer"
                                outlined
                                dense
                                auto-grow
                                rows="3"
                            />
                        </v-col>
                    </v-row>

                    <v-row class="mb-4">
                        <v-col cols="12">
                            <h3 class="mb-4">Tell us about any charity work you've done:</h3>
                            <v-textarea
                                v-model="form.charityWork"
                                placeholder="Your answer"
                                outlined
                                dense
                                auto-grow
                                rows="3"
                            />
                        </v-col>
                    </v-row>

                    <v-row class="mb-4">
                        <v-col cols="12">
                            <h3 class="mb-4">What would your dream job be if you weren't working for Synergy?</h3>
                            <v-textarea
                                v-model="form.dreamJob"
                                placeholder="Your answer"
                                outlined
                                dense
                                auto-grow
                                rows="3"
                            />
                        </v-col>
                    </v-row>

                    <v-row class="mb-4">
                        <v-col cols="12">
                            <h3 class="mb-4">What sports/exercise do you like? (include clubs/teams you play for or support)</h3>
                            <v-textarea
                                v-model="form.sportsAndExercise"
                                placeholder="Your answer"
                                outlined
                                dense
                                auto-grow
                                rows="3"
                            />
                        </v-col>
                    </v-row>

                    <v-row class="mb-4">
                        <v-col cols="12">
                            <h3 class="mb-4">How do you think your friends/family would describe you?</h3>
                            <v-textarea
                                v-model="form.howDescribed"
                                placeholder="Your answer"
                                outlined
                                dense
                                auto-grow
                                rows="3"
                            />
                        </v-col>
                    </v-row>

                    <v-row class="mb-4">
                        <v-col cols="12">
                            <h3 class="mb-4">How would you like to spend retirement?</h3>
                            <v-textarea
                                v-model="form.retirementPlans"
                                placeholder="Your answer"
                                outlined
                                dense
                                auto-grow
                                rows="3"
                            />
                        </v-col>
                    </v-row>

                    <v-row class="mb-4">
                        <v-col cols="12">
                            <h3 class="mb-4">Is there anything else you want included on your profile? (e.g. awards, publications, notable accomplishments)</h3>
                            <v-textarea
                                v-model="form.anythingElse"
                                placeholder="Your answer"
                                outlined
                                dense
                                auto-grow
                                rows="3"
                            />
                        </v-col>
                    </v-row>

                    <v-row class="mb-4">
                        <v-col cols="12">
                            <h3 class="mb-4">Please include links to any personal branding you may have (e.g. Instagram)</h3>
                            <v-textarea
                                v-model="form.personalBrandingLinks"
                                placeholder="Your answer"
                                outlined
                                dense
                                auto-grow
                                rows="3"
                            />
                        </v-col>
                    </v-row>

                    <div class="mt-5 pt-5" style="border-top:1px solid #e0e0e0">
                        <div class="d-flex justify-end">
                            <v-btn color="primary" :loading="submitLoading" @click="onSubmit">
                                Submit
                            </v-btn>
                        </div>
                    </div>
                </v-form>
            </v-card-text>
        </v-card>
    </v-dialog>
</template>

<script>
    import validationRules from '@/common/validationRules'
    import { mapActions } from 'vuex'

    const emptyForm = () => ({
        name: {
            firstName: '',
            lastName: ''
        },
        whereBorn: '',
        whereStudied: '',
        educationLevelAndSubject: '',
        dentalExperience: '',
        handleNervousPatients: '',
        unwindAfterWork: '',
        weekendHobbies: '',
        travelledTo: '',
        charityWork: '',
        dreamJob: '',
        sportsAndExercise: '',
        howDescribed: '',
        retirementPlans: '',
        anythingElse: '',
        personalBrandingLinks: ''
    })

    export default {
        name: 'DentistProfileFormDialog',
        props: ['isOpen', 'programme'],
        data: () => ({
            form: emptyForm(),
            submitLoading: false,
            requiredRules: validationRules.required,
            firstNameRules: validationRules.firstName,
            lastNameRules: validationRules.lastName
        }),
        computed: {
            dialogModel: {
                get () {
                    return this.isOpen
                },
                set (value) {
                    this.$emit('update:isOpen', value)
                }
            }
        },
        methods: {
            ...mapActions([
                'uploadDentistProfileForm', 'showAlert'
            ]),
            buildPayload () {
                return {
                    transitionProgrammeCourseId: this.programme && this.programme.id ? this.programme.id : 0,
                    firstName: this.form.name.firstName,
                    lastName: this.form.name.lastName,
                    whereWereYouBorn: this.form.whereBorn,
                    whereDidYouStudy: this.form.whereStudied,
                    educationLevelAndSubjects: this.form.educationLevelAndSubject,
                    dentalExperienceAndSpecialInterests: this.form.dentalExperience,
                    howDoYouHandleNervousPatients: this.form.handleNervousPatients,
                    howDoYouUnwindAfterWork: this.form.unwindAfterWork,
                    weekendHobbies: this.form.weekendHobbies,
                    whereHaveYouTravelledTo: this.form.travelledTo,
                    charityWork: this.form.charityWork,
                    dreamJobIfNotAtSynergy: this.form.dreamJob,
                    sportsAndExercise: this.form.sportsAndExercise,
                    howFriendsAndFamilyWouldDescribeYou: this.form.howDescribed,
                    retirementPlans: this.form.retirementPlans,
                    anythingElseForProfile: this.form.anythingElse,
                    personalBrandingLinks: this.form.personalBrandingLinks,
                    formType: 'DentistProfile'
                }
            },
            resetForm () {
                this.form = emptyForm()
            },
            closeDialog () {
                this.resetForm()
                this.$emit('update:isOpen', false)
            },
            async onSubmit () {
                const formRef = this.$refs.dentistProfileForm
                if (!formRef.validate()) {
                    this.showAlert({
                        text: 'Fill all the required fields please',
                        type: 'error'
                    })
                    return
                }

                this.submitLoading = true
                try {
                    const payload = this.buildPayload()
                    await this.uploadDentistProfileForm(payload)
                    this.$emit('saved')
                    this.closeDialog()
                    if (formRef && formRef.resetValidation) formRef.resetValidation()
                } catch (e) {
                    this.showAlert({
                        text: e.message || 'Error saving form',
                        type: 'error'
                    })
                } finally {
                    this.submitLoading = false
                }
            }
        }
    }
</script>
